import React, { useEffect, useState } from 'react';

const GENDER_OPTIONS = [
  { value: 'masculino', label: 'Masculino' },
  { value: 'femenino', label: 'Femenino' },
  { value: 'no-binario', label: 'No binario' },
  { value: 'prefiero-no-decirlo', label: 'Prefiero no decirlo' },
];

function ProfileEditView({ profile, ranks, onCancel, onSave }) {
  const [formState, setFormState] = useState({
    name: profile.name,
    email: profile.email,
    phone: profile.phone || '',
    rankId: profile.rankId,
    gender: profile.gender || '',
    password: '',
    confirmPassword: '',
    avatarUrl: profile.avatarUrl,
    avatarPreview: profile.avatarUrl,
  });

  useEffect(() => {
    return () => {
      if (formState.avatarPreview && formState.avatarPreview !== profile.avatarUrl) {
        URL.revokeObjectURL(formState.avatarPreview);
      }
    };
  }, [formState.avatarPreview, profile.avatarUrl]);

  const handleChange = (field) => (event) => {
    setFormState((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setFormState((prev) => {
      if (prev.avatarPreview && prev.avatarPreview !== profile.avatarUrl) {
        URL.revokeObjectURL(prev.avatarPreview);
      }
      return {
        ...prev,
        avatarUrl: file,
        avatarPreview: preview,
      };
    });
  };

  const clearAvatar = () => {
    setFormState((prev) => {
      if (prev.avatarPreview && prev.avatarPreview !== profile.avatarUrl) {
        URL.revokeObjectURL(prev.avatarPreview);
      }
      return {
        ...prev,
        avatarUrl: '',
        avatarPreview: '',
      };
    });
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailValid = emailRegex.test(formState.email.trim());
  const nameValid = formState.name.trim().length >= 2;
  const phoneDigits = formState.phone.replace(/\D/g, '');
  const phoneValid = phoneDigits.length >= 8;
  const passwordProvided = formState.password.trim().length > 0;
  const passwordValid = !passwordProvided || formState.password.trim().length >= 6;
  const passwordMatch = !passwordProvided || formState.password === formState.confirmPassword;
  const canSave = nameValid && emailValid && phoneValid && passwordValid && passwordMatch;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSave) return;
    onSave({
      name: formState.name,
      email: formState.email,
      phone: formState.phone.trim(),
      rankId: formState.rankId,
      gender: formState.gender,
      password: formState.password,
      avatarUrl: formState.avatarPreview,
    });
  };

  return (
    <div className="screen">
      <section className="section profile-edit-section">
        <div className="profile-edit-card">
          <header className="profile-edit-header" aria-label="Editar perfil">
            <div className="profile-edit-icon-title" aria-hidden="true">
              👤
            </div>
          </header>
          <form className="profile-edit-form" onSubmit={handleSubmit}>
            <div className="profile-edit-avatar">
              <label className="profile-edit-label" aria-label="Foto de perfil">
                Foto de perfil
              </label>
              <div className={formState.avatarPreview ? 'profile-edit-avatar-preview' : 'profile-edit-avatar-drop'}>
                {formState.avatarPreview ? (
                  <img src={formState.avatarPreview} alt="Vista previa" />
                ) : (
                  <span className="profile-edit-avatar-icon" aria-hidden="true">
                    📸
                  </span>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="profile-edit-avatar-input"
                  onChange={handleAvatarChange}
                  aria-label="Seleccionar foto de perfil"
                />
              </div>
              <button type="button" className="auth-link profile-edit-avatar-remove" onClick={clearAvatar} disabled={!formState.avatarPreview}>
                Quitar foto
              </button>
            </div>

            <label className="profile-edit-label" htmlFor="profile-name">
              Nombre
            </label>
            <input
              id="profile-name"
              type="text"
              className="profile-edit-input"
              value={formState.name}
              onChange={handleChange('name')}
              placeholder="Tu nombre"
              required
              minLength={2}
            />

            <label className="profile-edit-label" htmlFor="profile-email">
              Correo electrónico
            </label>
            <input
              id="profile-email"
              type="email"
              className={emailValid ? 'profile-edit-input' : 'profile-edit-input profile-edit-input-error'}
              value={formState.email}
              onChange={handleChange('email')}
              placeholder="nombre@correo.com"
              required
            />
            {!emailValid && <p className="profile-edit-error">Ingresa un correo válido.</p>}

            <label className="profile-edit-label" htmlFor="profile-phone">
              Teléfono
            </label>
            <input
              id="profile-phone"
              type="tel"
              className={phoneValid ? 'profile-edit-input' : 'profile-edit-input profile-edit-input-error'}
              value={formState.phone}
              onChange={handleChange('phone')}
              placeholder="Ej: +56 9 1234 5678"
              required
              inputMode="tel"
            />
            {!phoneValid && <p className="profile-edit-error">Ingresa un teléfono con al menos 8 dígitos.</p>}

            <div className="profile-edit-two-cols">
              <div>
                <label className="profile-edit-label" htmlFor="profile-rank">
                  Rango
                </label>
                <select
                  id="profile-rank"
                  className="profile-edit-input"
                  value={formState.rankId}
                  onChange={handleChange('rankId')}
                >
                  {ranks.map((rank) => (
                    <option key={rank.id} value={rank.id}>
                      {rank.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="profile-edit-label" htmlFor="profile-gender">
                  Género
                </label>
                <select
                  id="profile-gender"
                  className="profile-edit-input"
                  value={formState.gender}
                  onChange={handleChange('gender')}
                >
                  <option value="">Selecciona una opción</option>
                  {GENDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="profile-edit-two-cols">
              <div>
                <label className="profile-edit-label" htmlFor="profile-password">
                  Nueva contraseña
                </label>
                <input
                  id="profile-password"
                  type="password"
                  className={passwordValid ? 'profile-edit-input' : 'profile-edit-input profile-edit-input-error'}
                  value={formState.password}
                  onChange={handleChange('password')}
                  placeholder="••••••"
                />
                <p className="profile-edit-helper">Déjalo vacío para mantener la actual.</p>
              </div>
              <div>
                <label className="profile-edit-label" htmlFor="profile-confirm-password">
                  Confirmar contraseña
                </label>
                <input
                  id="profile-confirm-password"
                  type="password"
                  className={passwordMatch ? 'profile-edit-input' : 'profile-edit-input profile-edit-input-error'}
                  value={formState.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  placeholder="Repite la contraseña"
                />
              </div>
            </div>
            {!passwordValid && (
              <p className="profile-edit-error">La contraseña debe tener al menos 6 caracteres.</p>
            )}
            {!passwordMatch && (
              <p className="profile-edit-error">Las contraseñas no coinciden.</p>
            )}

            <div className="profile-edit-actions">
              <button type="button" className="pill-button pill-button-ghost" onClick={onCancel}>
                Cancelar
              </button>
              <button type="submit" className="pill-button pill-button-primary" disabled={!canSave}>
                Guardar cambios
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

export default ProfileEditView;
